import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signup(form.email, form.password, form.name, form.phone || undefined);
      navigate('/dashboard');
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'not_a_member') {
        setError(
          'Your email wasn\'t found in the 365reset membership. '
          + 'Make sure you\'re using the same email your 365reset membership is under. '
          + 'If you\'re not yet a member, visit 365reset.com to join.'
        );
      } else {
        setError(err.response?.data?.error || 'Could not set up your account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-reset-cream px-4">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <h1 className="font-serif text-2xl font-bold text-reset-brown">Set up your Dashboard</h1>
          <p className="text-reset-terracotta text-sm mt-1">
            Use the email your 365reset membership is under, and choose a password for this dashboard
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-reset-brown mb-1">Full name</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              autoComplete="name"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-reset-brown mb-1">
              Membership email
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              required
              autoComplete="email"
              placeholder="The email your 365reset membership is under"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-reset-brown mb-1">
              Choose a password
            </label>
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              required
              minLength={8}
              autoComplete="new-password"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              placeholder="Create a password for this dashboard"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-reset-brown mb-1">
              Phone <span className="text-gray-400">(optional — for SMS nudges)</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              autoComplete="tel"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-reset-terracotta text-white py-2.5 rounded-lg font-medium text-sm hover:opacity-90 disabled:opacity-50 transition"
          >
            {loading ? 'Verifying membership…' : 'Access my Dashboard'}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-6">
          Already set up your account?{' '}
          <Link to="/auth/login" className="text-reset-terracotta hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
